import React, { FunctionComponent, useMemo } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

import styled, { useTheme } from 'styled-components/native'

import { AppointmentBookedDetails } from '@lyrahealth-inc/shared-app-logic/src/models/calendar/CalendarV2'
import { MEETING_FORMATS } from '@lyrahealth-inc/shared-app-logic/src/models/session/Session'

import { BodyText } from '../../atoms/bodyText/BodyText'
import { BulletedList } from '../../atoms/bulletedList/BulletedList'
import { AlertLoudIcon } from '../../atoms/icons/AlertLoudIcon'
import { AttentionIcon } from '../../atoms/icons/AttentionIcon'
import { LoadingIndicator } from '../../atoms/icons/LoadingIndicator'
import { ProfileCircleIcon } from '../../atoms/icons/ProfileCircleIcon'
import { ScheduleIcon } from '../../atoms/icons/ScheduleIcon'
import { Link } from '../../atoms/link/Link'
import { BodyTextSize } from '../../styles/typeStyles'
import { openUrl } from '../../utils/expo-linking'
import { ThemeType } from '../../utils/themes/ThemeProvider'
import { tID } from '../../utils/utils'
import { Accordion } from '../accordion/Accordion'
import { InlineBanner } from '../inlineBanner/InlineBanner'

export type ProviderCalendarPopoverAppointmentBookedDetailsProps = {
  appointmentBookedDetails?: AppointmentBookedDetails
  isLoadingAppointmentDetails?: boolean
  showRiskAlerts?: boolean
  showModalitySelection?: boolean
  appointmentAddress?: string
  appointmentMeetingFormat?: MEETING_FORMATS
}

const TitleRow = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing['8px'],
}))

const Row = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'start',
  rowGap: theme.spacing['16px'],
}))

const ApptColumnContainer = styled.View({
  marginLeft: '32px',
  flexDirection: 'column',
})

const RowNameText = styled(BodyText)({
  width: '165px',
})

const RowValueText = styled(BodyText)({
  width: '197px',
})

const RiskAlertBannerRow = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  columnGap: theme.spacing['8px'],
}))

const RiskAlertDetailsRow = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'start',
  columnGap: theme.spacing['8px'],
  marginBottom: theme.spacing['8px'],
}))

const RiskAlertDetailsRowText = styled(BodyText)({
  width: '181px',
})

const RecommendedNextStepsContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  marginTop: theme.spacing['16px'],
}))

export const ProviderCalendarPopoverAppointmentBookedDetails: FunctionComponent<
  ProviderCalendarPopoverAppointmentBookedDetailsProps
> = ({
  appointmentBookedDetails,
  isLoadingAppointmentDetails = false,
  showRiskAlerts = true,
  showModalitySelection = false,
  appointmentAddress,
  appointmentMeetingFormat,
}) => {
  const theme = useTheme() as ThemeType
  const { formatMessage } = useIntl()
  const isMOS = appointmentBookedDetails?.isMos ?? false

  // For Military OneSource clients, the video modality is labeled "Virtual". Non-MOS clients and
  // non-video modalities are unchanged.
  const modalityValue =
    showModalitySelection && appointmentAddress
      ? appointmentAddress
      : isMOS && appointmentMeetingFormat === MEETING_FORMATS.VIDEO
        ? formatMessage({
            defaultMessage: 'Virtual',
            description: 'Virtual (video) appointment modality label for Military OneSource clients',
          })
        : appointmentBookedDetails?.modality

  const riskAlertBanner = useMemo(() => {
    const riskAlertTitles: Record<string, string> = {
      yellow: 'Low risk alert',
      amber: 'Moderate risk alert',
      red: 'High risk alert',
    }
    const riskLevel = appointmentBookedDetails?.highAlertObject?.riskLevel
    const bannerTitle = riskLevel ? (riskAlertTitles[riskLevel] ?? null) : null
    if (!appointmentBookedDetails?.highAlertObject) {
      return null
    } else {
      return (
        <InlineBanner
          marginBottom='8px'
          text={
            <BodyText
              text={formatMessage({
                defaultMessage: 'Review details and next steps below.',
                description: 'Text for risk alert banner',
              })}
              style={{
                marginLeft: appointmentBookedDetails?.highAlertObject?.riskLevel === 'red' ? '24px' : '32px',
              }}
              color={theme.colors.textError}
              size={BodyTextSize.SMALL}
            />
          }
          trailingLink={
            <Link
              color={theme.colors.textError}
              text={
                <BodyText
                  size={BodyTextSize.SMALL}
                  text={formatMessage({
                    defaultMessage: 'Learn more',
                    description: 'external link for risk alert banner',
                  })}
                  color={theme.colors.textError}
                />
              }
              onPress={() =>
                openUrl(
                  'https://provider-support.lyrahealth.com/hc/en-us/articles/27188838479507-Clinical-Safety-Resources',
                )
              }
              underline
            />
          }
          backgroundColor={
            appointmentBookedDetails?.highAlertObject?.riskLevel === 'red'
              ? theme.colors.backgroundError
              : theme.colors.backgroundWarning
          }
          title={
            <RiskAlertBannerRow>
              {appointmentBookedDetails?.highAlertObject?.riskLevel === 'red' ? (
                <AlertLoudIcon fillColor={theme.colors.iconWarning} />
              ) : (
                <AttentionIcon fillColor={theme.colors.iconWarning} />
              )}
              <BodyText text={bannerTitle} size={BodyTextSize.LARGE} color={theme.colors.textError} />
            </RiskAlertBannerRow>
          }
        />
      )
    }
  }, [
    appointmentBookedDetails?.highAlertObject,
    formatMessage,
    theme.colors.backgroundError,
    theme.colors.backgroundWarning,
    theme.colors.iconWarning,
    theme.colors.textError,
  ])

  const riskAlertDetails = useMemo(() => {
    if (!appointmentBookedDetails?.highAlertObject) {
      return null
    } else {
      const notApplicableText = formatMessage({
        defaultMessage: 'N/A',
        description: 'Not applicable text',
      })
      return (
        <Accordion
          testIDPrefix='ProviderCalendarPopoverAppointmentBookedDetails-riskAlertDetails'
          title={
            <BodyText
              text={formatMessage({
                defaultMessage: 'Risk alert details',
                description: 'Title for the risk alert details accordion',
              })}
              size={BodyTextSize.LARGE}
            />
          }
          content={
            <>
              <BodyText
                text={formatMessage({
                  defaultMessage: 'The client provided these details within the last week',
                  description: 'Text for the risk alert details accordion',
                })}
                size={BodyTextSize.SMALL}
                style={{ marginBottom: '16px' }}
              />
              <RiskAlertDetailsRow>
                <RiskAlertDetailsRowText
                  text={formatMessage({
                    defaultMessage: 'Key risks shared:',
                    description: 'Key risks shared row name',
                  })}
                  size={BodyTextSize.SMALL}
                  testID={tID('ProviderCalendarPopoverAppointmentBookedDetails-keyRiskSI-rowName')}
                />
                <RiskAlertDetailsRowText
                  text={
                    appointmentBookedDetails?.highAlertObject?.keyRiskSI
                      ? appointmentBookedDetails?.highAlertObject?.keyRiskSI
                      : notApplicableText
                  }
                  size={BodyTextSize.SMALL}
                  color={theme.colors.textError}
                  testID={tID('ProviderCalendarPopoverAppointmentBookedDetails-keyRiskSI-text')}
                />
              </RiskAlertDetailsRow>
              <RiskAlertDetailsRow>
                <RiskAlertDetailsRowText
                  text={formatMessage({
                    defaultMessage: 'Suicidal thoughts in the last 2 weeks:',
                    description: 'row name for suicidal thoughts in the last 2 weeks',
                  })}
                  size={BodyTextSize.SMALL}
                  testID={tID('ProviderCalendarPopoverAppointmentBookedDetails-siInPast2Weeks-rowName')}
                />
                <RiskAlertDetailsRowText
                  text={
                    appointmentBookedDetails?.highAlertObject?.siInPast2Weeks
                      ? appointmentBookedDetails?.highAlertObject?.siInPast2Weeks
                      : notApplicableText
                  }
                  size={BodyTextSize.SMALL}
                  color={theme.colors.textError}
                  testID={tID('ProviderCalendarPopoverAppointmentBookedDetails-siInPast2Weeks-text')}
                />
              </RiskAlertDetailsRow>
              <RiskAlertDetailsRow>
                <RiskAlertDetailsRowText
                  text={formatMessage({
                    defaultMessage: 'Frequency of suicidal thoughts:',
                    description: 'row name for frequency of suicidal thoughts',
                  })}
                  size={BodyTextSize.SMALL}
                  testID={tID('ProviderCalendarPopoverAppointmentBookedDetails-siFrequency-rowName')}
                />
                <RiskAlertDetailsRowText
                  text={
                    appointmentBookedDetails?.highAlertObject?.siFrequency
                      ? appointmentBookedDetails?.highAlertObject?.siFrequency
                      : notApplicableText
                  }
                  size={BodyTextSize.SMALL}
                  color={theme.colors.textError}
                  testID={tID('ProviderCalendarPopoverAppointmentBookedDetails-siFrequency-text')}
                />
              </RiskAlertDetailsRow>
              <RiskAlertDetailsRow>
                <RiskAlertDetailsRowText
                  text={formatMessage({
                    defaultMessage: 'Plan to end life:',
                    description: 'row name for plan to end life',
                  })}
                  size={BodyTextSize.SMALL}
                  testID={tID('ProviderCalendarPopoverAppointmentBookedDetails-siPlan-rowName')}
                />
                <RiskAlertDetailsRowText
                  text={
                    appointmentBookedDetails?.highAlertObject?.siPlan
                      ? appointmentBookedDetails?.highAlertObject?.siPlan
                      : notApplicableText
                  }
                  size={BodyTextSize.SMALL}
                  color={theme.colors.textError}
                  testID={tID('ProviderCalendarPopoverAppointmentBookedDetails-siPlan-text')}
                />
              </RiskAlertDetailsRow>
              <RiskAlertDetailsRow>
                <RiskAlertDetailsRowText
                  text={formatMessage({
                    defaultMessage: 'Intent within 24 hours:',
                    description: 'row name for intent within 24 hours',
                  })}
                  size={BodyTextSize.SMALL}
                  testID={tID('ProviderCalendarPopoverAppointmentBookedDetails-siPlanInTheNext24Hours-rowName')}
                />
                <RiskAlertDetailsRowText
                  text={
                    appointmentBookedDetails?.highAlertObject?.siPlanInTheNext24Hours
                      ? appointmentBookedDetails?.highAlertObject?.siPlanInTheNext24Hours
                      : notApplicableText
                  }
                  size={BodyTextSize.SMALL}
                  color={theme.colors.textError}
                  testID={tID('ProviderCalendarPopoverAppointmentBookedDetails-siPlanInTheNext24Hours-text')}
                />
              </RiskAlertDetailsRow>
              <RiskAlertDetailsRow>
                <RiskAlertDetailsRowText
                  text={formatMessage({
                    defaultMessage: 'Taken steps to end their life:',
                    description: 'row name for taken steps to end their life',
                  })}
                  size={BodyTextSize.SMALL}
                  testID={tID('ProviderCalendarPopoverAppointmentBookedDetails-siStepsTaken-rowName')}
                />
                <RiskAlertDetailsRowText
                  text={
                    appointmentBookedDetails?.highAlertObject?.siStepsTaken
                      ? appointmentBookedDetails?.highAlertObject?.siStepsTaken
                      : notApplicableText
                  }
                  size={BodyTextSize.SMALL}
                  color={theme.colors.textError}
                  testID={tID('ProviderCalendarPopoverAppointmentBookedDetails-siStepsTaken-text')}
                />
              </RiskAlertDetailsRow>

              <RiskAlertDetailsRow>
                <RiskAlertDetailsRowText
                  text={formatMessage({
                    defaultMessage: 'Hospitalized for mental health reasons:',
                    description: 'row name for hospitalized for mental health reasons',
                  })}
                  size={BodyTextSize.SMALL}
                  testID={tID('ProviderCalendarPopoverAppointmentBookedDetails-hospitalized-rowName')}
                />
                <RiskAlertDetailsRowText
                  text={
                    appointmentBookedDetails?.highAlertObject?.hospitalized
                      ? appointmentBookedDetails?.highAlertObject?.hospitalized
                      : notApplicableText
                  }
                  size={BodyTextSize.SMALL}
                  color={theme.colors.textError}
                  testID={tID('ProviderCalendarPopoverAppointmentBookedDetails-hospitalized-text')}
                />
              </RiskAlertDetailsRow>

              <RecommendedNextStepsContainer>
                <BodyText
                  text={formatMessage({
                    defaultMessage: 'Recommended next steps',
                    description: 'Recommended next steps title',
                  })}
                  size={BodyTextSize.LARGE}
                />
                <BodyText
                  text={formatMessage({
                    defaultMessage: '1. Offer the soonest available appointment',
                    description: 'Bullet point number 1 for recommended next steps',
                  })}
                  size={BodyTextSize.SMALL}
                  style={{ marginBottom: '8px' }}
                />
                <BodyText
                  text={formatMessage({
                    defaultMessage: '2. Prioritize holding the session',
                    description: 'Bullet point number 2 for recommended next steps',
                  })}
                  size={BodyTextSize.SMALL}
                  style={{ marginBottom: '8px' }}
                />
                <BodyText
                  text={formatMessage({
                    defaultMessage: '3. Conduct your own risk assessment/safety planning during the session',
                    description: 'Bullet point number 3 for recommended next steps',
                  })}
                  size={BodyTextSize.SMALL}
                  style={{ marginBottom: '8px' }}
                />
                <BodyText
                  text={formatMessage({
                    defaultMessage: '4. Make use of clinical consultation if needed',
                    description: 'Bullet point number 4 for recommended next steps',
                  })}
                  size={BodyTextSize.SMALL}
                />
                <BodyText
                  text={formatMessage({
                    defaultMessage: 'If client does not show or cancels',
                    description: 'If client does not show or cancels text',
                  })}
                  style={{ marginTop: '16px' }}
                  size={BodyTextSize.LARGE}
                />
                <BulletedList
                  data={[
                    { value: 'Reach out to the client at a clinically appropriate cadence' },
                    { value: 'Attempt to re-engage them in care' },
                    { value: 'Document outreach attempts' },
                  ]}
                  size={BodyTextSize.SMALL}
                  spaceBetween={8}
                />
                <BodyText
                  text={
                    <FormattedMessage
                      defaultMessage='If you are still unsuccessful in contacting this client, submit a <link>Client Referral Form</link> so our Care Navigator Team can follow up.'
                      description='text describing what to do if you are still unsuccessful in contacting the client'
                      values={{
                        link: (text: string) => (
                          <Link
                            text={text}
                            underline
                            size={BodyTextSize.SMALL}
                            onPress={() =>
                              openUrl(
                                'https://provider-support.lyrahealth.com/hc/en-us/requests/new?ticket_form_id=360000119987',
                              )
                            }
                          />
                        ),
                      }}
                    />
                  }
                  style={{ marginTop: '16px' }}
                  size={BodyTextSize.SMALL}
                />
                <BodyText
                  text={formatMessage({ defaultMessage: 'Note', description: 'Note text' })}
                  size={BodyTextSize.LARGE}
                  style={{ marginTop: '16px' }}
                />
                <BodyText
                  text={formatMessage({
                    defaultMessage:
                      'Our Care Navigator Team will conduct extended and repeated outreach to assess this client’s appropriateness for outpatient care. If the Care Navigator Team successfully contacts this client, you will receive a follow up email with details of their risk assessment.',
                    description:
                      'A note describing what will happen if the Care Navigator Team successfully contacts the client',
                  })}
                  size={BodyTextSize.SMALL}
                />
              </RecommendedNextStepsContainer>
            </>
          }
          chevronSize={24}
          backgroundColor={theme.colors.backgroundElement}
          style={{ padding: '16px', borderRadius: '16px' }}
        />
      )
    }
  }, [appointmentBookedDetails?.highAlertObject, formatMessage, theme.colors.backgroundElement, theme.colors.textError])

  return (
    <>
      {isLoadingAppointmentDetails ? (
        <LoadingIndicator size={'small'} />
      ) : (
        <>
          {showRiskAlerts && riskAlertBanner}
          {showRiskAlerts && riskAlertDetails}
          <TitleRow>
            <ScheduleIcon fillColor={theme.colors.iconDefault} size={24} />
            <BodyText
              text={
                <FormattedMessage
                  defaultMessage='Appointment Scheduled For:'
                  description='Title for the appointment scheduled for block'
                />
              }
              size={BodyTextSize.LARGE}
            />
          </TitleRow>
          <ApptColumnContainer>
            <BodyText
              text={appointmentBookedDetails?.apptStartTime}
              size={BodyTextSize.SMALL}
              testID={tID('ProviderCalendarPopoverAppointmentBookedDetails-appointmentDetails-apptStartTime-text')}
            />
          </ApptColumnContainer>
          <Row>
            <ApptColumnContainer>
              <Row>
                <RowNameText
                  text={<FormattedMessage defaultMessage='Appointment type:' description='Appointment type row name' />}
                  size={BodyTextSize.SMALL}
                />
                <RowValueText
                  text={
                    isMOS ? (
                      appointmentBookedDetails?.treatmentType
                    ) : (
                      <FormattedMessage
                        defaultMessage='{typeOfPatient} | {treatmentType}'
                        values={{
                          typeOfPatient: appointmentBookedDetails?.typeOfPatient,
                          treatmentType: appointmentBookedDetails?.treatmentType,
                        }}
                        description='Appointment type value'
                      />
                    )
                  }
                  size={BodyTextSize.SMALL}
                  testID={tID(
                    'ProviderCalendarPopoverAppointmentBookedDetails-appointmentDetails-appointmentType-text',
                  )}
                />
              </Row>
              {!isMOS && (
                <>
                  <Row>
                    <RowNameText
                      text={<FormattedMessage defaultMessage='Booked by:' description='Booked by row name' />}
                      size={BodyTextSize.SMALL}
                    />
                    <RowValueText
                      text={
                        appointmentBookedDetails?.appointmentForChild ? (
                          <FormattedMessage
                            defaultMessage='Parent/Guardian'
                            description='Text saying that the appointment was booked by the Parent/Guardian text'
                          />
                        ) : (
                          <FormattedMessage
                            defaultMessage='Self'
                            description='Text saying that the appointment was booked by the client themselves'
                          />
                        )
                      }
                      size={BodyTextSize.SMALL}
                      testID={tID('ProviderCalendarPopoverAppointmentBookedDetails-appointmentDetails-bookedBy-text')}
                    />
                  </Row>
                  <Row>
                    <RowNameText
                      text={<FormattedMessage defaultMessage='Primary need:' description='Primary need row name' />}
                      size={BodyTextSize.SMALL}
                    />
                    <RowValueText
                      text={appointmentBookedDetails?.primaryNeed}
                      size={BodyTextSize.SMALL}
                      testID={tID(
                        'ProviderCalendarPopoverAppointmentBookedDetails-appointmentDetails-primaryNeed-text',
                      )}
                    />
                  </Row>
                </>
              )}
              <Row>
                <RowNameText
                  text={
                    showModalitySelection ? (
                      <FormattedMessage defaultMessage='Modality:' description='modality row name' />
                    ) : (
                      <FormattedMessage
                        defaultMessage='Meeting preference:'
                        description='meeting preference row name'
                      />
                    )
                  }
                  size={BodyTextSize.SMALL}
                />
                <RowValueText
                  text={modalityValue}
                  size={BodyTextSize.SMALL}
                  testID={tID(
                    showModalitySelection && appointmentAddress
                      ? 'ProviderCalendarPopoverAppointmentBookedDetails-appointmentDetails-address-text'
                      : 'ProviderCalendarPopoverAppointmentBookedDetails-appointmentDetails-meetingPreference-text',
                  )}
                />
              </Row>
              <Row>
                <RowNameText
                  text={<FormattedMessage defaultMessage='Customer:' description='Customer row name' />}
                  size={BodyTextSize.SMALL}
                />
                <RowValueText
                  text={appointmentBookedDetails?.customer}
                  size={BodyTextSize.SMALL}
                  testID={tID('ProviderCalendarPopoverAppointmentBookedDetails-appointmentDetails-customer-text')}
                />
              </Row>
            </ApptColumnContainer>
          </Row>
          <TitleRow>
            <ProfileCircleIcon fillColor={theme.colors.iconDefault} size={24} />
            <BodyText
              text={
                <FormattedMessage
                  defaultMessage='Client Information:'
                  description='Title for the client information block'
                />
              }
              size={BodyTextSize.LARGE}
            />
          </TitleRow>
          <Row>
            <ApptColumnContainer>
              {isMOS ? (
                <>
                  {appointmentBookedDetails?.mosId && (
                    <Row>
                      <RowNameText
                        text={
                          <FormattedMessage defaultMessage='Case Number:' description='Leidos case number row name' />
                        }
                        size={BodyTextSize.SMALL}
                      />
                      <RowValueText
                        text={appointmentBookedDetails.mosId}
                        size={BodyTextSize.SMALL}
                        testID={tID('ProviderCalendarPopoverAppointmentBookedDetails-appointmentDetails-mosId-text')}
                      />
                    </Row>
                  )}
                  {appointmentBookedDetails?.authorizationNumber && (
                    <Row>
                      <RowNameText
                        text={<FormattedMessage defaultMessage='Auth #:' description='Authorization number row name' />}
                        size={BodyTextSize.SMALL}
                      />
                      <RowValueText
                        text={appointmentBookedDetails.authorizationNumber}
                        size={BodyTextSize.SMALL}
                        testID={tID(
                          'ProviderCalendarPopoverAppointmentBookedDetails-appointmentDetails-authorizationNumber-text',
                        )}
                      />
                    </Row>
                  )}
                  {appointmentBookedDetails?.lyraCode && (
                    <Row>
                      <RowNameText
                        text={<FormattedMessage defaultMessage='Lyra Code:' description='Lyra code row name' />}
                        size={BodyTextSize.SMALL}
                      />
                      <RowValueText
                        text={appointmentBookedDetails.lyraCode}
                        size={BodyTextSize.SMALL}
                        testID={tID('ProviderCalendarPopoverAppointmentBookedDetails-appointmentDetails-lyraCode-text')}
                      />
                    </Row>
                  )}
                </>
              ) : (
                <>
                  {appointmentBookedDetails?.appointmentForChild && (
                    <>
                      <Row>
                        <RowNameText
                          text={<FormattedMessage defaultMessage='Child name:' description='Child name row name' />}
                          size={BodyTextSize.SMALL}
                        />
                        <RowValueText
                          // eslint-disable-next-line formatjs/no-literal-string-in-jsx
                          text={`${appointmentBookedDetails?.child_firstName} ${appointmentBookedDetails?.child_lastName}`}
                          size={BodyTextSize.SMALL}
                          testID={tID(
                            'ProviderCalendarPopoverAppointmentBookedDetails-appointmentDetails-childName-text',
                          )}
                        />
                      </Row>
                      <Row>
                        <RowNameText
                          text={
                            <FormattedMessage
                              defaultMessage='Child date of birth:'
                              description='Child date of birth row name'
                            />
                          }
                          size={BodyTextSize.SMALL}
                        />
                        <RowValueText
                          text={appointmentBookedDetails?.child_clientDob}
                          size={BodyTextSize.SMALL}
                          testID={tID(
                            'ProviderCalendarPopoverAppointmentBookedDetails-appointmentDetails-childDob-text',
                          )}
                        />
                      </Row>
                    </>
                  )}
                  <Row>
                    <RowNameText
                      text={
                        appointmentBookedDetails?.appointmentForChild ? (
                          <FormattedMessage defaultMessage='Parent date of birth:' description='Parent DOB row name' />
                        ) : (
                          <FormattedMessage defaultMessage='Date of Birth:' description='Client DOB row name' />
                        )
                      }
                      size={BodyTextSize.SMALL}
                    />
                    <RowValueText
                      text={appointmentBookedDetails?.clientDob?.replace('DOB: ', '')}
                      size={BodyTextSize.SMALL}
                      testID={tID('ProviderCalendarPopoverAppointmentBookedDetails-appointmentDetails-clientDob-text')}
                    />
                  </Row>
                  <Row>
                    <RowNameText
                      text={<FormattedMessage defaultMessage='Contact:' description='Client contact row name' />}
                      size={BodyTextSize.SMALL}
                    />
                    <RowValueText
                      text={appointmentBookedDetails?.clientContactInfo?.replace(' | ', '\n')}
                      size={BodyTextSize.SMALL}
                      testID={tID(
                        'ProviderCalendarPopoverAppointmentBookedDetails-appointmentDetails-clientContactInfo-text',
                      )}
                    />
                  </Row>
                </>
              )}
            </ApptColumnContainer>
          </Row>
        </>
      )}
    </>
  )
}
